/**
 * Datadog Logs SDK Integration
 *
 * Connects the frontend logger to Datadog Logs SDK.
 * Handles ad blocker failures gracefully with error suppression.
 */

import { datadogLogs, type LogsEvent } from '@datadog/browser-logs';
import { getDatadogConfig, isDevOrUat } from './datadogConfig';
import { redactSensitiveFields } from './redaction';
import { LogEntry, LogSink } from './frontendLogger';

let initialized = false;
let failureCount = 0;
const MAX_FAILURES = 5;

/**
 * Initializes Datadog Logs SDK if configuration is available.
 * Safe to call multiple times (idempotent).
 */
export const initDatadogLogs = (): void => {
  if (initialized) {
    return;
  }

  const config = getDatadogConfig();

  if (!config) {
    if (isDevOrUat()) {
      console.warn(
        '[Observability] Datadog Logs configuration missing. ' +
          'Logs will not be sent to Datadog. Set VITE_DATADOG_* environment variables to enable.'
      );
    }
    return;
  }

  try {
    datadogLogs.init({
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      forwardErrorsToLogs: false,
      sessionSampleRate: 100,
      beforeSend: (event: LogsEvent) => {
        const redacted = redactSensitiveFields(event as Record<string, unknown>);
        Object.assign(event, redacted);
        return true;
      },
    });

    initialized = true;
  } catch (error) {
    if (isDevOrUat()) {
      console.error('[Observability] Failed to initialize Datadog Logs:', error);
    }
  }
};

/**
 * Returns whether Datadog Logs has been successfully initialized.
 */
export const isDatadogLogsInitialized = (): boolean => {
  return initialized;
};

/**
 * Creates a log sink that forwards entries to Datadog Logs SDK.
 * Handles ad blocker failures with backoff.
 */
export const createDatadogLogSink = (): LogSink => {
  return (entry: LogEntry): void => {
    if (!initialized) {
      return;
    }

    // Stop trying if we've hit too many failures (likely ad blocker)
    if (failureCount >= MAX_FAILURES) {
      return;
    }

    try {
      const logContext = {
        ...entry.context,
        timestamp: entry.timestamp,
      };

      switch (entry.level) {
        case 'debug':
          datadogLogs.logger.debug(entry.message, logContext);
          break;
        case 'info':
          datadogLogs.logger.info(entry.message, logContext);
          break;
        case 'warn':
          datadogLogs.logger.warn(entry.message, logContext);
          break;
        case 'error':
          datadogLogs.logger.error(entry.message, logContext);
          break;
      }

      // Reset failure count on success
      failureCount = 0;
    } catch {
      // Increment failure count (likely ad blocker)
      failureCount++;

      if (failureCount === MAX_FAILURES && isDevOrUat()) {
        console.warn(
          '[Observability] Datadog Logs requests appear to be blocked. ' +
            'This may be due to an ad blocker. Stopping further attempts.'
        );
      }
    }
  };
};

/**
 * Sets user context for Datadog Logs sessions.
 * Standard fields (id, name, email) plus custom attributes (role, company).
 * All logs will include this user information.
 * Context is automatically redacted for sensitive fields.
 */
export const setLogsUser = (user: {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  company?: string;
}): void => {
  if (!initialized) {
    return;
  }

  const safeUser = redactSensitiveFields(user);
  datadogLogs.setUser(safeUser);
};

/**
 * Clears user context from Datadog Logs sessions.
 * Call this on logout to stop associating logs with a user.
 */
export const clearLogsUser = (): void => {
  if (!initialized) {
    return;
  }

  datadogLogs.clearUser();
};

/**
 * Resets Datadog Logs state (for testing purposes only).
 */
export const resetDatadogLogsState = (): void => {
  initialized = false;
  failureCount = 0;
};
